import { KnockProvider } from "@knocklabs/react";
import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { knock } from "@src/appV2/environment/config";
import { type ReactNode } from "react";

import {
  KnockGuideProviderWithRefetch,
  type KnockGuideProviderWithRefetchProps,
} from "./KnockGuideProviderWithRefetch";

interface KnockProviderWrapperProps {
  children: ReactNode;
  guideConsumers: ReactNode;
  userId: string | undefined;
  knockToken: string | undefined;
  onUserTokenExpiring: () => Promise<string | undefined>;
  targetParams?: KnockGuideProviderWithRefetchProps["targetParams"];
}

/**
 * Keeps the authenticated application children mounted at a stable React
 * position while conditionally mounting Knock guide consumers inside the Knock
 * React SDK providers (`KnockProvider` → `KnockGuideProvider`).
 *
 * Adapted from `cbh-mobile-app`'s `KnockProviderWrapper`. We skip `KnockFeedProvider`
 * for now since the admin frontend doesn't yet have an in-app notification feed — only
 * guides are needed for the initial launch (rebrand announcement blast).
 */
export function KnockProviderWrapper(props: KnockProviderWrapperProps) {
  const { children, guideConsumers, userId, knockToken, onUserTokenExpiring, targetParams } = props;

  return (
    <>
      {children}
      {isDefined(knockToken) ? (
        <KnockProvider
          apiKey={environmentConfig.REACT_APP_KNOCK_PUBLIC_API_KEY}
          user={{ id: userId }}
          userToken={knockToken}
          onUserTokenExpiring={onUserTokenExpiring}
        >
          <KnockGuideProviderWithRefetch
            readyToTarget
            channelId={knock.channels.guides}
            targetParams={targetParams}
          >
            {guideConsumers}
          </KnockGuideProviderWithRefetch>
        </KnockProvider>
      ) : null}
    </>
  );
}
